import React, { useState } from "react";
import { User, Mail, Phone, MessageSquare } from "lucide-react";

const StepContact = ({ onNext, onPrev, initialData }) => {
  const [contact, setContact] = useState({
    firstName: initialData.contact?.firstName || "",
    lastName: initialData.contact?.lastName || "",
    email: initialData.contact?.email || "",
    phone: initialData.contact?.phone || "",
    details: initialData.details || "", // Mapping details/reason here
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!contact.firstName) newErrors.firstName = "First name is required";
    if (!contact.lastName) newErrors.lastName = "Last name is required";
    if (!contact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email))
      newErrors.email = "Valid email is required";
    if (!contact.phone) newErrors.phone = "Phone is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onNext({ contact, details: contact.details });
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-6 font-alata">
        Your Contact Information
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              First Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={contact.firstName}
                onChange={(e) =>
                  setContact({ ...contact, firstName: e.target.value })
                }
                className={`w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.firstName ? "border-red-500" : "border-slate-300 dark:border-slate-600"} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                placeholder="John"
              />
            </div>
            {errors.firstName && (
              <p className="text-red-500 text-xs">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Last Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={contact.lastName}
                onChange={(e) =>
                  setContact({ ...contact, lastName: e.target.value })
                }
                className={`w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.lastName ? "border-red-500" : "border-slate-300 dark:border-slate-600"} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                placeholder="Doe"
              />
            </div>
            {errors.lastName && (
              <p className="text-red-500 text-xs">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="email"
              value={contact.email}
              onChange={(e) =>
                setContact({ ...contact, email: e.target.value })
              }
              className={`w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.email ? "border-red-500" : "border-slate-300 dark:border-slate-600"} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
              placeholder="john.doe@example.com"
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Phone Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="tel"
              value={contact.phone}
              onChange={(e) =>
                setContact({ ...contact, phone: e.target.value })
              }
              className={`w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.phone ? "border-red-500" : "border-slate-300 dark:border-slate-600"} rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
              placeholder="(555) 123-4567"
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-xs">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Additional Details / Special Requests
          </label>
          <div className="relative">
            <div className="absolute top-3 left-3 pointer-events-none">
              <MessageSquare className="h-5 w-5 text-slate-400" />
            </div>
            <textarea
              rows={4}
              value={contact.details}
              onChange={(e) =>
                setContact({ ...contact, details: e.target.value })
              }
              className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
              placeholder="Tell us more about your event..."
            />
          </div>
        </div>

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrev}
            className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
          >
            Next Step
          </button>
        </div>
      </form>
    </div>
  );
};

export default StepContact;
